import {
  CertificationFormData,
  FilingInformationData,
  FincenReportStatus,
  FincenReportSummaryResponse,
  FincenResponse,
  FincenUpdateResponse,
  PaymentInformation,
  PropertyData,
  ReportingPartyFormData,
  Transferee,
  Transferor,
} from 'features/files/fincen-reporting/types';
import { handleError } from 'utilities/utilities';
import { getApiHost } from './host-by-env';

export async function getFincenReport(fileId: string, token: string): Promise<FincenResponse> {
  const url = `${getApiHost()}fincenReportService/${fileId}/fincen-report`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenResponse;
}

export async function updateFincenReportingPartyAPI(
  reportId: string,
  formData: ReportingPartyFormData,
  token: string,
): Promise<FincenUpdateResponse> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/party`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(formData),
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenUpdateResponse;
}

export async function updateFincenReporteDateAPI(
  reportId: string,
  formData: FilingInformationData,
  token: string,
): Promise<FincenUpdateResponse> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/reportdate`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(formData),
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenUpdateResponse;
}

export async function updateFincenPropertiesAPI(
  reportId: string,
  formData: PropertyData[],
  token: string,
): Promise<FincenUpdateResponse> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/property`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(formData),
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenUpdateResponse;
}

export async function updateFincenTransfereeAPI(
  reportId: string,
  formData: Transferee[],
  token: string,
): Promise<FincenUpdateResponse> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/transferee`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(formData),
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenUpdateResponse;
}

export async function updateFincenTransferorAPI(
  reportId: string,
  formData: Transferor[],
  token: string,
): Promise<FincenUpdateResponse> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/transferor`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(formData),
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenUpdateResponse;
}

export async function updateFincenPaymentAPI(
  reportId: string,
  formData: PaymentInformation,
  token: string,
): Promise<FincenUpdateResponse> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/payment`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(formData),
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenUpdateResponse;
}

export async function downloadFincenReportXML(reportId: string, token: string): Promise<Response> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/xml`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/xml',
    },
  });

  if (!response.ok) {
    throw new Error('Error downloading FinCEN report XML.');
  }

  return response;
}

export async function updateFincenCertifierAPI(
  reportId: string,
  formData: CertificationFormData,
  token: string,
): Promise<FincenUpdateResponse> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/certifier`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(formData),
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenUpdateResponse;
}

export async function getFincenReportSummary(reportId: string, token: string): Promise<FincenReportSummaryResponse> {
  const url = `${getApiHost()}fincenReportService/report/${reportId}/summary`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result as FincenReportSummaryResponse;
}

export async function updateFincenReporStatusAPI(
  fileId: string,
  request: FincenReportStatus,
  token: string,
): Promise<boolean> {
  const url = `${getApiHost()}fincenReportService/${fileId}/fincen-report-status`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  const json = await response.json();
  handleError(json);
  return json.result as boolean;
}
